import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useQueryClient, useMutation, type UseMutationOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {deleteCommentPath} from '../api'

type Props = {
  plantId: string
  commentsCategory: string
}

export type DeleteCommentParameter = {
  id: string
  commentCategoryInstanceId: string
}

export const useDeleteComment = (
  {plantId, commentsCategory}: Props,
  queryKeyPrefix: string = 'comments',
  options?: Omit<UseMutationOptions<void, AxiosError, DeleteCommentParameter>, 'mutationFn'>
) => {
  const {t} = useTranslation()
  const client = useQueryClient()

  return useMutation<void, AxiosError, DeleteCommentParameter>({
    ...options,
    mutationFn: async ({id, commentCategoryInstanceId}) => {
      const path = deleteCommentPath({
        plantId,
        commentsCategory,
        commentCategoryInstanceId,
        commentId: id
      })

      await ApiClient.axiosInstance.delete(path)
    },
    onSuccess: (...args) => {
      spawnSuccessToast(t('comments.message.deleteSuccess'))
      void client.invalidateQueries({queryKey: [queryKeyPrefix]})
      options?.onSuccess?.(...args)
    },
    onError: (...args) => {
      spawnErrorToast(t('comments.message.failedToDeleteComment'))
      options?.onError?.(...args)
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args)
    }
  })
}
